/** Third party imports */
import { computed, inject, Injectable, signal } from '@angular/core';
import isEqual from 'lodash/isEqual';

/** Constant Imports */
import { FormBuilder, FormGroup } from '@angular/forms';
import {
    CREATE_USER_STEPS,
    INPUT_INFORMATION_STEP_LAYOUT,
    USER_ROLES,
} from '@features/users/constants';
import {
    AllKeys,
    CreateTerritoryStepRoles,
    CreateUserFlowDraft,
    SelectDealerStepRoles,
    UiDraft,
    UserRoles,
} from '@features/users/interfaces';
import { comparePasswords, formBuilder } from '@shared/utils';
import { DraftSubject } from '../draft-subject/draft-subject.service';

/**
 * Create user steps service
 *
 * This service is responsible for managing the create user flow.
 * It holds the state of the current step and the draft data.
 *
 * @description
 * This service is used to manage the create user flow. It holds the state of the current step and the draft data.
 *
 * **NOTE TO DEVELOPERS**
 *
 * Private class members with `_` prefix are meant to be modified inside the service only.
 * It has equivalent public class members (without `_` prefix) that are meant to be used by the components
 *
 * @example
 * ```typescript
 * // Private members (internal use only)
 * private _currentStepIndex = signal(0);
 * private _draft = signal({});
 *
 * // Public members (component use)
 * public currentStepIndex = this._currentStepIndex.asReadonly();
 * public draft = this._draft.asReadonly();
 * ```
 */
@Injectable({
    providedIn: 'root',
})
export class CreateUserStepsService {
    /**
     * Injected draft subject used to emit draft changes to be persisted to the database.
     */
    private _draftSubject = inject(DraftSubject);

    /**
     * Inject form builder to temporarily construct a form for validation
     */
    private _fb = inject(FormBuilder);

    /**
     * Internal signal holding the main step configurations for the create user flow.
     * Should only be modified within this service.
     */
    private _steps = signal(CREATE_USER_STEPS);

    /**
     * Computed sub-step count for the currently active main step.
     * Derives the count from `_stepsSubStepsMap` using the current step ID,
     * eliminating the need for an external `effect()` to keep it in sync.
     */
    private _currentStepSubStepsCount = computed(
        () => this._stepsSubStepsMap()[this.currentStepId()] ?? 0,
    );

    /**
     * A static map of step ID → sub-step count, registered once from the component.
     * This lets us look up sub-step counts for ANY step without waiting for Angular to render it.
     */
    private _stepsSubStepsMap = signal<Record<string, number>>({});

    /**
     * Tracks the index of the currently active step. Defaults to `0`.
     */
    private _currentStepIndex = signal(0);

    /**
     * Tracks the index of the currently active sub-step. Defaults to `0`.
     */
    private _currentSubStepIndex = signal(0);

    /**
     * Last visited step index. This allows users to click the stepper buttons directly but
     * it would only allow them if the step.index they clicked is less than to this value
     */
    private _lastVisitedStepIndex = signal(0);

    /**
     * The selected user type/role to be created.
     * Defaults to `null` when no user type has been selected yet.
     */
    private _userType = signal<{ id: UserRoles; label: string } | null>(null);

    /**
     * Source of truth for the current draft data across all steps.
     * Holds partial or complete step data for the selected user role.
     *
     * @description
     * Accumulates step data as the user progresses through the flow.
     * May be incomplete until all steps are finalized.
     */
    private _draft = signal<Record<string, unknown>>({});

    /**
     * Stores the last known valid and complete step data.
     * Used to compare against current step data in `updateStep` to detect
     * actual changes and avoid unnecessary network requests on every step navigation.
     */
    private _savedDraft = signal<Record<string, unknown>>({});

    /**
     * Stores the last successfully synced draft data.
     * Compared against `_draft` to determine if actual changes exist before
     * triggering a save — prevents duplicate API calls on window blur when
     * data has not changed.
     *
     * @remarks Only used during window `onblur` to guard against redundant draft saves.
     */
    private _lastSyncedDraft = signal<Record<string, unknown>>({});

    /**
     * Tracks whether an async operation is currently in progress,
     * such as validation, form submission, or any other async task.
     */
    private _isLoading = signal(false);

    /**
     * Tracks the checked state of the confirmation checkbox on the preview details step
     */
    public isConfirmationChecked = signal(false);

    /**
     * Stores the actual File object for the profile picture to be uploaded to Filestack.
     * This is kept separate from the draft to avoid serializing large binary data.
     */
    private _profilePictureFile = signal<File | null>(null);

    /**
     * Stores the local preview URL/base64 for profile picture UI rendering across steps.
     * Kept out of draft persistence so image data is not synced before final submit.
     */
    private _profilePicturePreview = signal<string | null>(null);

    /**
     * Public access to the profile picture file
     */
    public profilePictureFile = this._profilePictureFile.asReadonly();

    /**
     * Public access to local profile picture preview.
     */
    public profilePicturePreview = this._profilePicturePreview.asReadonly();

    /**
     * Updates the profile picture file
     */
    public setProfilePictureFile(file: File | null): void {
        this._profilePictureFile.set(file);
    }

    /**
     * Updates the local profile picture preview URL/base64.
     */
    public setProfilePicturePreview(preview: string | null): void {
        this._profilePicturePreview.set(preview);
    }

    /**
     * Read-only computed signal of the current steps configuration.
     * Resolves the steps based on the selected user type.
     * Falls back to the default `none` steps if no user type is selected.
     */
    public steps = computed(() => {
        const userTypeId = this._userType()?.['id'];
        if (!userTypeId) {
            return this._steps()['none'];
        }
        return this._steps()[userTypeId];
    });

    /**
     * Read-only signal of the currently active step index.
     */
    public currentStepIndex = this._currentStepIndex.asReadonly();

    /**
     * Read-only signal of the currently active sub-step index.
     */
    public currentSubStepIndex = this._currentSubStepIndex.asReadonly();

    /**
     * Read-only signal of the last visited step index
     */
    public lastVisitedStepIndex = this._lastVisitedStepIndex.asReadonly();

    /**
     * Read-only signal for the substeps map of all steps
     */
    public stepsSubStepsMap = this._stepsSubStepsMap.asReadonly();

    /**
     * Read-only signal of the currently selected user type to be created.
     */
    public selectedUserType = this._userType.asReadonly();

    /**
     * Read-only signal indicating whether an async operation is in progress,
     * such as validation or form submission.
     */
    public isLoading = this._isLoading.asReadonly();

    /**
     * Read-only signal of the current in-progress draft state.
     */
    public draft = this._draft.asReadonly();

    /**
     * Read-only signal of the last successfully synced draft state.
     */
    public lastSyncedDraft = this._lastSyncedDraft.asReadonly();

    /**
     * The label of the currently active step.
     * Derived from the current step index and the steps array.
     */
    public currentStepLabel = computed(() => {
        return this.steps()[this.currentStepIndex()].label;
    });

    /**
     * The ID of the currently active step.
     * Derived from the current step index and the steps array.
     */
    public currentStepId = computed(() => {
        return this.steps()[this.currentStepIndex()].id;
    });

    /**
     * Navigates to the previous step in the create user flow.
     * Resets the user type if navigating back to the first step.
     * Restores the sub-step index to the last sub-step of the previous step.
     * Both step and sub-step indices are updated atomically to prevent UI flicker.
     */
    public goPrevStep(): void {
        if (this.currentStepIndex() - 1 === 0) {
            this._userType.set(null);
        }

        const prevStepIndex = this.currentStepIndex() - 1;
        const prevStepId = this.steps()[prevStepIndex]?.id;
        const prevStepSubStepCount = this._stepsSubStepsMap()[prevStepId] ?? 0;

        // Set both atomically before any rendering happens — no flicker
        this._currentStepIndex.set(prevStepIndex);
        this._currentSubStepIndex.set(prevStepSubStepCount > 0 ? prevStepSubStepCount - 1 : 0);
    }

    /**
     * Navigates to the next step in the create user flow.
     * Resets the sub-step index back to 0 upon navigation.
     * Exits early if the current step is the last one.
     */
    public goNextStep(): void {
        if (this.currentStepIndex() + 1 === this.steps().length) {
            return;
        }
        this._currentStepIndex.update((currentStepIndex) => currentStepIndex + 1);
        // Set the sub step index to 0 when we go to the next step
        this._currentSubStepIndex.set(0);

        // Set this current step as the last visited step index ONLY if the current step index is greater than the last visited step index
        if (this.currentStepIndex() > this.lastVisitedStepIndex()) {
            this._lastVisitedStepIndex.set(this.currentStepIndex());
        }

        // If the current idx has step error: true in it, we need to set it to false
        if (this.steps()[this.currentStepIndex()].error) {
            this.setStepError(this.steps()[this.currentStepIndex()].id, false);
        }
    }

    /**
     * Navigates to the next sub-step within the current step.
     * Exits early if the current sub-step is the last one.
     */
    public goNextSubStep(): void {
        if (this.currentSubStepIndex() + 1 === this._currentStepSubStepsCount()) {
            return;
        }
        this._currentSubStepIndex.update((currentSubStepIndex) => currentSubStepIndex + 1);
    }

    /**
     * Navigates to the previous sub-step within the current step.
     * Clamps to 0 if the previous index would reach or go below zero.
     */
    public goPrevSubStep(): void {
        if (this.currentSubStepIndex() - 1 === 0) {
            this._currentSubStepIndex.set(0);
            return;
        }
        this._currentSubStepIndex.update((currentSubStepIndex) => currentSubStepIndex - 1);
    }

    /**
     * Navigates to a specific step in the create user flow.
     * Resets the sub-step index back to 0 upon navigation.
     * Exits early if the resolved step index is out of bounds.
     *
     * @param step - A valid step key for the selected user role/type to be created
     * @param subStepIdx - An optional sub step index for this current step. Defaults to `0`
     */
    public goToStep(
        step: AllKeys<CreateUserFlowDraft[Exclude<UserRoles, 'none'>]>,
        subStepIdx: number = 0,
    ): void {
        const stepIdx = this.steps().findIndex((s) => s.id === step);
        if (stepIdx < 0 || stepIdx >= this.steps().length) {
            return;
        }
        this._currentStepIndex.set(stepIdx);

        // If subStepIdx is provided, that means we navigate to the current step and to the specific sub step in this step
        this._currentSubStepIndex.set(subStepIdx);

        // Set this current step as the last visited step index
        this._lastVisitedStepIndex.set(stepIdx);
    }

    /**
     * Navigates to a specific step using step index
     *
     * @param stepIdx The step index value to navigate to
     *
     * @remarks
     * Used specifically when user clicks the stepper button directly NOT the previous and next button navigations
     */
    public goToStepIdx(stepIdx: number): void {
        if (stepIdx < 0 || stepIdx >= this.steps().length) {
            return;
        }
        this._currentStepIndex.set(stepIdx);
        // Set the sub step index to 0 when we go to the previous step
        this._currentSubStepIndex.set(0);

        // If the current idx has step error: true in it, we need to set it to false
        if (this.steps()[stepIdx].error) {
            this.setStepError(this.steps()[stepIdx].id, false);
        }
    }

    /**
     * Sets the user type/role to be created.
     * So we can strictly type the every step data for that given user role which is to be created
     */
    public setUserType(userType: { id: UserRoles; label: string }): void {
        this._userType.set(userType);
        this._draft.set({}); // Clear the draft when new user is selected
        this._currentStepIndex.set(0); // Reset the step index
        this.setLastSyncedDraft({}); // Reset the last synced draft
        this._savedDraft.set({}); // Reset the saved draft
        this._lastVisitedStepIndex.set(0); // Reset the last visited step index
    }

    /**
     * Registers a map of step IDs to their sub-step counts.
     * Should be called once from the component so the service knows
     * how many sub-steps each step has without waiting for rendering.
     */
    public registerStepSubSteps(map: Record<string, any[]>): void {
        const countMap: Record<string, number> = {};
        for (const [stepId, subSteps] of Object.entries(map)) {
            countMap[stepId] = subSteps?.length ?? 0;
        }
        this._stepsSubStepsMap.set(countMap);
    }

    /**
     * Sets loading prop
     */
    public setIsLoading(isLoading: boolean): void {
        this._isLoading.set(isLoading);
    }

    /**
     * Set last synced draft
     */
    public setLastSyncedDraft(draft: Record<string, unknown>): void {
        this._lastSyncedDraft.set(draft);
    }

    /**
     * Update draft state for a selected step and by user type
     *
     * `S` is the step key for a given user role e.g for `"Dealer"` they have `"input-information"`, and `"create-territory"`
     *  so the `S` here must be either of the two just for type safety
     */
    public updateStep<R extends UserRoles, S extends keyof CreateUserFlowDraft[Exclude<R, 'none'>]>(
        role: R,
        step: S,
        data: CreateUserFlowDraft[Exclude<R, 'none'>][S],
        isMainStep: boolean = true,
    ): void {
        // We need to check if the data for this step really changed
        // if not, we don't need to update the draft nor the draft api
        const currentDraft = this.getSavedDraft(role); // _savedDraft is the draft data that contains COMPLETE steps data
        if (currentDraft && currentDraft[step] && isEqual(currentDraft[step], data)) {
            // Just go the next step immediately or the next substep
            if (isMainStep) {
                this.goNextStep();
            } else {
                if (this.currentSubStepIndex() + 1 === this._currentStepSubStepsCount()) {
                    this.goNextStep();
                } else {
                    this.goNextSubStep();
                }
            }
            return;
        }
        this._draft.update((prev) => {
            return {
                ...prev,
                [step]: { ...((prev as any)[step] ?? {}), ...data },
            };
        });
        // Update the _savedDraft which holds the complete step data (not partial)

        this._savedDraft.set(this._draft());

        /**
         * We also need to set the lastSyncDraft for this value so when user navigates back to the previous step
         *  we can compare the data and determine if we need to perform a network request or not
         */
        this.setLastSyncedDraft(this._draft());

        /**
         * A callback function to be triggered after the draft is updated
         *
         * - If `isMainStep` is true, we increment the current step index (the main stepper)
         * - If `isMainStep` is false, we increment the current sub step index (the sub stepper of a current step)
         */
        const cb = isMainStep ? this.goNextStep : this.goNextSubStep;

        /**
         * An rxjs subject that would make a network request when a new step data is created
         * @see create-user-steps-api.service.ts where we subscribe to this subject
         */
        this._draftSubject.next(this._draft() as unknown as CreateUserFlowDraft, cb.bind(this));
    }

    /**
     * Update a specific property on a specific step for a specific/selected user role to be created
     *
     * @description
     * This does not perform network request at all, used in onBlur functionalities where we save the data to the in memory draft on every field blur
     *
     * **NOTE**: `_role` property here is solely for type-inferrence. Do not remove
     */
    public updateStepProperty<
        R extends UserRoles,
        S extends keyof CreateUserFlowDraft[Exclude<R, 'none'>],
        K extends keyof CreateUserFlowDraft[Exclude<R, 'none'>][S],
    >(_role: R, step: S, property: K, value: CreateUserFlowDraft[Exclude<R, 'none'>][S][K]): void {
        // 1). Check if draft has ID, if not, generate one
        if (!this._draft()?.['id']) {
            this._draft.update((prev) => {
                return {
                    ...prev,
                    id: this.generateUUID(),
                    role: this.selectedUserType()?.id as keyof CreateUserFlowDraft,
                    createdAt: new Date().toISOString(),
                };
            });
        }

        // 2). Check if the value is actually different first
        const currentStepData = (this._draft() as any)[step];
        if (currentStepData && currentStepData[property] === value) {
            return;
        }

        this._draft.update((prev) => {
            return {
                ...prev,
                [step]: { ...((prev as any)[step] ?? {}), [property]: value },
            };
        });
    }

    /**
     * Updates a top-level property of the draft for the specified user role.
     * Use this for properties that are not part of a specific step, such as profile picture.
     *
     * @param _role - User role (used for type inference)
     * @param property - The top-level property name
     * @param value - The value to update
     */
    public updateDraftProperty<R extends Exclude<UserRoles, 'none'>>(
        _role: R,
        property: keyof CreateUserFlowDraft[R],
        value: unknown,
    ): void {
        // Ensure ID and basic metadata exist
        if (!this._draft()?.['id']) {
            this._draft.update((prev) => {
                return {
                    ...prev,
                    id: this.generateUUID(),
                    role: this.selectedUserType()?.id as keyof CreateUserFlowDraft,
                    createdAt: new Date().toISOString(),
                };
            });
        }

        this._draft.update((prev) => {
            return {
                ...prev,
                [property]: value,
            };
        });
    }

    /**
     * Silently patches a step's data in the in-memory draft WITHOUT:
     * - Navigating to the next step/sub-step
     * - Triggering a network request via _draftSubject
     * - Updating _lastSyncedDraft (so `shouldSaveDraft()` can detect the change on tab blur)
     *
     * Use this for cases like territory map `onDone` where you want the data saved in memory
     * so that `visibilitychange` / `beforeunload` handlers can persist it.
     */
    public patchDraftStep<
        R extends UserRoles,
        S extends keyof CreateUserFlowDraft[Exclude<R, 'none'>],
    >(_role: R, step: S, data: Partial<CreateUserFlowDraft[Exclude<R, 'none'>][S]>): void {
        // Check if draft has ID, if not, generate one
        if (!this._draft()?.['id']) {
            this._draft.update((prev) => {
                return {
                    ...prev,
                    id: this.generateUUID(),
                    role: this.selectedUserType()?.id as keyof CreateUserFlowDraft, // Stamp the role onto the draft
                    createdAt: new Date().toISOString(), // Initial creation timestamp
                };
            });
        }

        // Guard: Check if the data is actually different first
        const currentStepData = (this._draft() as any)[step] ?? {};
        if (isEqual({ ...currentStepData, ...data }, currentStepData)) {
            return;
        }

        this._draft.update((prev) => {
            return {
                ...prev,
                [step]: { ...((prev as any)[step] ?? {}), ...data },
            };
        });
    }

    /**
     * Get the current draft (the in memory draft while the user types or selects even **WITHOUT** CLICKING THE NEXT BUTTON YET).
     *
     * @returns {Partial<CreateUserFlowDraft[R]>} The current draft for the seleced user role
     *
     * **NOTE**: `_role` property here is solely for type-inferrence. Do not remove
     */
    public getDraft<R extends UserRoles>(_role: R): Partial<UiDraft<Exclude<R, 'none'>>> {
        return this._draft() as Partial<UiDraft<Exclude<R, 'none'>>>;
    }

    /**
     * Get saved draft (the in memory draft still **BUT** only when the user clicks next on it). This data contains the **COMPLETE** steps data
     *
     * Why this approach?
     *
     * I need to keep track of the progress using draft #1 but I need to make sure that only when the data is not the same with draft #2
     * then that's when I make a network request.  Draft #1 holds the current state, Draft #2 holds the actual and complete step state
     *
     * On scenario where the user did not complete the form then when the user reloads the page we have another method that handles that one
     * @see create-user.componnet.ts file @method onBeforeUnload
     *
     * **NOTE**: `_role` property here is solely for type-inferrence. Do not remove
     * @private It's only used in this service. Used to avoid redundant network requests when the data is the same
     */
    private getSavedDraft<R extends UserRoles>(_role: R): Partial<UiDraft<Exclude<R, 'none'>>> {
        return this._savedDraft() as Partial<UiDraft<Exclude<R, 'none'>>>;
    }

    /**
     * Loads an existing draft into the service state.
     *
     * @param role The role associated with the draft
     * @param draft The full draft object to load
     */
    public loadDraft<R extends UserRoles>(role: R, draft: Record<string, unknown>): void {
        const roleConfig = USER_ROLES.find((r) => r.id === role);
        const userType = {
            id: role,
            label: roleConfig?.label ?? role,
        };

        // 1. Set the User Type (this triggers computed 'steps' updates)
        this._userType.set(userType);

        // 2. Hydrate all draft-related signals
        this._draft.set(draft);
        this._savedDraft.set(draft);
        this.setLastSyncedDraft(draft);
    }
    /**
     * Disables all stepper steps after the specified step index.
     *
     * @param stepIdx - The index of the invalid step. All steps from this index
     * onwards will be disabled to prevent the user from navigating forward.
     *
     * @remarks
     * This is used in conjunction with the stepper navigation guard. While navigation
     * is normally allowed to any previously visited step, this method adds an additional
     * constraint — if the current step being edited is invalid, the user cannot navigate
     * to steps beyond the invalid one until it is corrected.
     *
     * @example
     * // User is on step 2 and clears a required field
     * // Steps 2, 3, 4 will be disabled until the field is filled again
     * this.setInvalidStep(2);
     */
    public setInvalidStep(stepIdx: number): void {
        const role = this._userType()?.id;
        if (!role) return;

        const steps = this._steps()[role].map((step, i) => {
            return {
                ...step,
                disabled: i <= stepIdx ? false : true,
            };
        });

        this._steps.update((prev) => {
            return {
                ...prev,
                [role]: steps,
            };
        });
    }

    /**
     * Re-enables all stepper steps for the current user role.
     *
     * @remarks
     * Called when the current step becomes valid again (e.g. user fills in
     * previously cleared required fields). This restores full stepper navigation
     * by removing the disabled state from all steps.
     *
     * @see {@link setInvalidStep} for the inverse operation
     */
    public setValidStep(): void {
        const role = this._userType()?.id;
        if (!role) return;

        const steps = this._steps()[role].map((step, i) => {
            return {
                ...step,
                disabled: false,
            };
        });

        this._steps.update((prev) => {
            return {
                ...prev,
                [role]: steps,
            };
        });
    }

    /**
     * Sets the `error` flag on a specific step identified by its ID.
     *
     * @param stepId - The ID of the step to update
     * @param hasError - Whether the step should be marked as having an error
     *
     * @remarks
     * Immutably updates the `_steps` signal so the stepper UI reflects the error state.
     * This is used when navigating backwards to mark incomplete steps with a visual error indicator.
     */
    public setStepError(stepId: string, hasError: boolean): void {
        const role = this._userType()?.id;
        if (!role) return;

        this._steps.update((allSteps) => {
            const roleSteps = allSteps[role];
            const updatedRoleSteps = roleSteps.map((step) =>
                step.id === stepId ? { ...step, error: hasError } : step,
            );
            return { ...allSteps, [role]: updatedRoleSteps };
        });
    }

    /**
     * Checks whether the current step's draft data is complete and valid.
     *
     * @returns `true` if the current step data is complete, `false` otherwise
     *
     * @remarks
     * Delegates to the existing private validators per step:
     * - `input-information`: reconstructs the form and checks validity
     * - `create-territory`: checks both sub-steps via `validateCreateTerritoryStepData()`
     * - `select-dealer`: checks via `validateSelectDelearStepData()`
     *
     * Steps without specific validators (e.g. `create-new-user`) are assumed complete.
     */
    public isCurrentStepComplete(): boolean {
        const role = this._userType()?.id as UserRoles;
        if (!role) return true;

        const currentStepId = this.currentStepId();

        if (currentStepId === 'input-information') {
            const stepData = this.getDraft(role)['input-information'];
            if (!stepData) return false;

            const inputFieldsByRole = (INPUT_INFORMATION_STEP_LAYOUT as any)[role];
            const tempForm = formBuilder(this._fb, inputFieldsByRole, [comparePasswords]);
            tempForm.patchValue(stepData as any);
            return tempForm.valid;
        }

        if (currentStepId === 'create-territory') {
            const { stepOneValid, stepTowValid } = this.validateCreateTerritoryStepData();
            return stepOneValid && stepTowValid;
        }

        if (currentStepId === 'select-dealer') {
            return this.validateSelectDelearStepData();
        }

        // For steps without specific validators, assume complete
        return true;
    }

    /**
     * Validate all the selected draft data step by step to then check where we are going to redirect the user
     *
     * @param {string[]} populatedStepsInDraft An array of all populated steps for the selected draft
     *
     * @remarks
     * The data is retrieved inside the `create-user.service` so we don't need to pass it from the invoking component.
     *
     * The following checks are to be executed:
     * - It checks all steps one at a time
     * - If a specific step has incomplete data, we redirect the user to that step
     * - If last populated step has complete and valid data then user navigates to the previous step
     * and removes some data then saves the draft. When the user goes back to this specific draft, they will be redirected to the very step
     * the validator found an incomplete or incorrect data
     * - If ALL steps are valid, redirect the user to the last step (or preview/summary step)
     *
     * @public
     */
    public async validateDraftDataOnLoad(populatedStepsInDraft: string[]): Promise<void> {
        const role = this._userType()?.id as UserRoles;
        if (!role) return;

        for (const step of populatedStepsInDraft) {
            if (step === 'input-information') {
                const stepData = this.getDraft(role)['input-information'];

                const inputFieldsByRole = (INPUT_INFORMATION_STEP_LAYOUT as any)[role];

                const tempForm = formBuilder(this._fb, inputFieldsByRole, [comparePasswords]);

                tempForm.patchValue(stepData as any);

                const isValid = this.isFormValid(tempForm);

                // If not valid then we go to the input-information step
                if (!isValid) {
                    this.goToStep('input-information');
                    return;
                }
            } else if (step === 'create-territory') {
                const { stepOneValid, stepTowValid } = this.validateCreateTerritoryStepData();

                if (!stepOneValid) {
                    this.goToStep('create-territory', 0);
                    return;
                }

                if (!stepTowValid) {
                    this.goToStep('create-territory', 1);
                    return;
                }

                // Both sub-steps valid — proceed to the next step by index
                const createTerritoryIdx = this.steps().findIndex(
                    (s) => s.id === 'create-territory',
                );
                if (createTerritoryIdx >= 0 && createTerritoryIdx + 1 < this.steps().length) {
                    this.goToStepIdx(createTerritoryIdx + 1);
                    // Also update last visited so the stepper knows this step was reached
                    this._lastVisitedStepIndex.set(createTerritoryIdx + 1);
                    return;
                }
            } else if (step === 'select-dealer') {
                const isValid = this.validateSelectDelearStepData();

                if (!isValid) {
                    this.goToStep('select-dealer');
                    return;
                }
            }
        }

        // If all passes then go the the last step
        const lastPopulatedStep = populatedStepsInDraft[populatedStepsInDraft.length - 1];
        this.goToStep(
            lastPopulatedStep as AllKeys<CreateUserFlowDraft[Exclude<UserRoles, 'none'>]>,
        );
    }

    /**
     * Clears and resets all draft-related state in the create user service.
     *
     * @remarks
     * This method performs a full reset of the create user flow, including:
     * - Draft data (current and saved)
     * - Last synced draft snapshot
     * - Selected user type
     * - Step and sub-step indices
     * - Sub-step count
     *
     * This should be called when:
     * - The user completes the create user flow
     * - The user explicitly discards the draft
     * - The user navigates away from the create user flow
     *
     * @note
     * Step indices and sub-step count are reset to `0` here but will be
     * re-registered by the component on initialization.
     */
    public clearDraft(): void {
        // 1. Clear the draft data
        this._draft.set({});
        this._savedDraft.set({});
        this.setLastSyncedDraft({});
        this.setProfilePictureFile(null);
        this.setProfilePicturePreview(null);

        // 2. Clear the selected user type
        this._userType.set(null);

        // 3. Reset the current step index (re-registered by the component on init)
        this._currentStepIndex.set(0);

        // 4. Reset the current sub-step index (re-registered by the component on init)
        this._currentSubStepIndex.set(0);
    }

    /**
     * A function that checks whether the termporary populated form is valid or not
     *
     * @returns Whether the form is valid
     */
    private isFormValid(form: FormGroup): boolean {
        form.markAllAsTouched();

        return form.valid;
    }

    /**
     * Validate create-territory step data when user clicks on the draft and it contains create-territory data
     *
     * @remarks
     * We validate the create-territory step data so we can navigate user to this step or to the next step
     * if the step data is valid and complete
     */
    private validateCreateTerritoryStepData(): { stepOneValid: boolean; stepTowValid: boolean } {
        const role = this._userType()?.id as CreateTerritoryStepRoles;
        const territoryData = this.getDraft(role)?.['create-territory'];

        if (!territoryData) {
            return { stepOneValid: false, stepTowValid: false };
        }

        const validKeysForSubStepOne = [
            'coordinates',
            'state',
            'color',
            'area',
            'createdAt',
        ] as const;
        const validKeysForSubStepTwo = ['territoryName'] as const;

        // Loop through the territory data and check if the coordinates, territory name, state, color, area, createdAt are not null
        // also the assignedTo - but DO NOT for now
        const isSubStepOneValid = validKeysForSubStepOne.every(
            (key) =>
                territoryData[key as keyof typeof territoryData] !== null &&
                territoryData[key as keyof typeof territoryData] !== undefined &&
                !!territoryData[key as keyof typeof territoryData],
        );

        const isSubStepTwoValid = validKeysForSubStepTwo.every(
            (key) =>
                territoryData[key as keyof typeof territoryData] !== null &&
                territoryData[key as keyof typeof territoryData] !== undefined &&
                !!territoryData[key as keyof typeof territoryData],
        );
        return { stepOneValid: isSubStepOneValid, stepTowValid: isSubStepTwoValid };
    }

    /**
     * Validate select-dealer step data
     *
     * @remarks
     * Checks if selectedDealers property has at least one item
     * Returns true if there is, false otherwise
     */
    private validateSelectDelearStepData(): boolean {
        const role = this._userType()?.id as SelectDealerStepRoles;
        const selectDealersData = this.getDraft(role)?.['select-dealer'];

        if (!selectDealersData) return false;

        return (
            (selectDealersData['selectedDealers'] &&
                Array.isArray(selectDealersData['selectedDealers']) &&
                selectDealersData['selectedDealers'].length > 0) ??
            false
        );
    }

    /**
     * Generate a random UUID
     *
     * @returns {string} A random UUID
     */
    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }
}
